import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, Award, Star, Crown, Medal, Zap, Shield, Users, Lightbulb, Handshake, GraduationCap, Brain } from 'lucide-react';

const Winners = () => {
  const winners = [
    {
      category: "Best Fintech Start Up",
      winner: "Franc",
      icon: <Zap className="h-8 w-8" />,
      description: "Recognizing the most promising new company demonstrating exceptional potential in financial technology"
    },
    {
      category: "Best RegTech Award",
      winner: "Smile ID",
      icon: <Shield className="h-8 w-8" />,
      description: "Honoring solutions that effectively address regulatory compliance challenges in financial services"
    },
    {
      category: "Cryptocurrency Victor",
      winner: "Money Badger",
      icon: <Trophy className="h-8 w-8" />,
      description: "Acknowledging excellence in cryptocurrency and blockchain-based financial solutions"
    },
    {
      category: "Excellence in Payments",
      winner: "Kazang",
      icon: <Medal className="h-8 w-8" />,
      description: "Recognizing innovative payment solutions that are reshaping how we transfer and manage money"
    },
    {
      category: "Enhancing Financial Inclusion",
      winner: "UCT Financial Innovation Hub with the Interledger Foundation",
      icon: <Users className="h-8 w-8" />,
      description: "Celebrating initiatives that bring financial services to underserved communities"
    },
    {
      category: "Green Finance Award",
      winner: "Nav» Earth",
      icon: <Star className="h-8 w-8" />,
      description: "Honoring financial solutions that promote environmental sustainability and combat climate change"
    },
    {
      category: "FinTech Innovation of the Year",
      winner: "Lula",
      icon: <Lightbulb className="h-8 w-8" />,
      description: "Recognizing the most groundbreaking innovation that is transforming the financial technology landscape"
    },
    {
      category: "The Collaboration of 2024",
      winner: "Bizcash & KingPrice Life Insurance & Old Mutual",
      icon: <Handshake className="h-8 w-8" />,
      description: "Celebrating successful partnerships between traditional financial institutions and fintech innovators"
    },
    {
      category: "Financial Literacy Champion",
      winner: "Koshiek Karan",
      icon: <GraduationCap className="h-8 w-8" />,
      description: "Recognizing initiatives that excel in promoting financial education and awareness"
    },
    {
      category: "Emerging Thought Leader of the Year",
      winner: "Koshiek Karan",
      icon: <Brain className="h-8 w-8" />,
      description: "Celebrating individuals who have made significant contributions to fintech thought leadership"
    },
    {
      category: "Fintech Entrepreneur of 2024",
      winner: "Rahul Jain",
      icon: <Star className="h-8 w-8" />,
      description: "Recognizing outstanding entrepreneurial achievement in financial technology"
    },
    {
      category: "Lifetime Achievement Award",
      winner: "Coen Jonker",
      icon: <Crown className="h-8 w-8" />,
      description: "Honoring individuals who have made lasting contributions to the development of fintech in South Africa"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <motion.div
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://titc.io/wp-content/uploads/2024/08/Award.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70" />
        </motion.div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <div className="flex justify-center mb-8">
              <Trophy className="h-20 w-20" style={{ color: '#C1B3A3' }} />
            </div>
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white mb-6">
              Congratulations to Our
              <span className="block" style={{ color: '#C1B3A3' }}>
                2025 Winners
              </span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-200 mb-8">
              Celebrating Excellence in South African FinTech
            </p>
          </motion.div>
        </div>
      </section>

      {/* Winners Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6" style={{ color: '#1A1A1A' }}>
              The Winners of SA FinTech Awards 2025
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These exceptional organizations and individuals have demonstrated outstanding innovation, 
              leadership, and impact in South Africa's financial technology sector
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {winners.map((winner, index) => (
              <motion.div
                key={winner.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="p-8">
                  <div className="flex items-center justify-center w-16 h-16 rounded-full mb-6 mx-auto"
                       style={{ backgroundColor: 'rgba(193, 179, 163, 0.2)' }}>
                    <div style={{ color: '#C1B3A3' }}>{winner.icon}</div>
                  </div>
                  
                  <h3 className="text-xl font-bold mb-4 text-center" style={{ color: '#1A1A1A' }}>
                    {winner.category}
                  </h3>
                  
                  <div className="text-center mb-6">
                    <div className="inline-block px-6 py-3 rounded-full text-white font-bold text-lg"
                         style={{ backgroundColor: '#C1B3A3' }}>
                      <Trophy className="h-5 w-5 inline-block mr-2" />
                      {winner.winner}
                    </div>
                  </div>
                  
                  <p className="text-gray-600 text-center text-sm leading-relaxed">
                    {winner.description}
                  </p>
                </div>
                
                <div className="h-2 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Celebration Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Award className="h-16 w-16 mx-auto mb-8" style={{ color: '#C1B3A3' }} />
            <h2 className="text-4xl font-bold mb-6" style={{ color: '#C1B3A3' }}>
              A Night to Remember
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto mb-12">
              The SA FinTech Awards 2025 celebrated the incredible innovation, dedication, and vision 
              that continues to drive South Africa's financial technology sector forward. Each winner 
              represents the pinnacle of excellence in their respective categories, contributing to a 
              more inclusive, innovative, and sustainable financial future for all South Africans.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
              <div className="text-center">
                <div className="text-4xl font-bold mb-2" style={{ color: '#C1B3A3' }}>12</div>
                <div className="text-gray-300">Award Categories</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2" style={{ color: '#C1B3A3' }}>500+</div>
                <div className="text-gray-300">Industry Leaders</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2" style={{ color: '#C1B3A3' }}>∞</div>
                <div className="text-gray-300">Future Possibilities</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Thank You Section */}
      <section className="py-20" style={{ backgroundColor: 'rgba(193, 179, 163, 0.1)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold mb-6" style={{ color: '#1A1A1A' }}>
              Thank You to Our Partners
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              The SA FinTech Awards 2025 would not have been possible without the support of our 
              incredible sponsors, partners, judges, and the entire South African FinTech community. 
              Together, we continue to build a brighter financial future for Africa.
            </p>
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <p className="text-gray-700 italic">
                "These awards represent more than recognition – they celebrate the spirit of innovation 
                that drives our industry forward and the collaborative efforts that make meaningful 
                change possible."
              </p>
              <p className="mt-4 font-semibold" style={{ color: '#C1B3A3' }}>
                - The SA FinTech Awards Team
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Winners;